import { Button, Gapped, SidePage, Spinner, ScrollContainer } from "@skbkontur/react-ui";
import React from "react";

import { EmployerNormalizedTag, ImportEmployerDocumentsOptions } from "../../EmployeeServiceTypes";
import { useMetrics } from "../../../Controls/Internals/Metrics/MetricsProviderContext";
import {
    EMPLOYER_DOCUMENT_IMPORT,
    EMPLOYER_DOCUMENTS_CALL_SOURCE,
    EMPLOYER_DOCUMENTS_CANCEL,
    EMPLOYER_DOCUMENTS_CLOSE,
    EMPLOYER_DOCUMENTS_COUNT,
    EMPLOYER_DOCUMENTS_WARNING_SIZE,
} from "../../../Controls/metricsAction";
import { Markdown } from "../../../Controls/Helpers/Markdown/Markdown";

import {
    DescriptionContainer,
    DocumentsContainer,
    EmptyContainer,
    EmptyContainerInner,
    EmptyText,
    Hint,
    IconContainer,
    Ul,
    Warning,
} from "./EmployerDocumentsSidePage.styles";
import { EmployerDocumentsTable } from "./EmployerDocumentsTable/EmployerDocumentsTable";
import EmptyIcon from "./Icons/EmptyIcon.svg";
import { EmployerDocumentsFilterContainer as EmployerDocumentsFilter } from "./EmployerDocumentsFilter/EmployerDocumentsFilterContainer";

export interface EmployerDocumentsSidePageProps {
    showSideBar: boolean;
    loading: boolean;
    documentTags: EmployerNormalizedTag[];
    options?: ImportEmployerDocumentsOptions;
    onClose: () => void;
    onSubmit: (tag: EmployerNormalizedTag) => void;
    activePage: number;
    onPageChange: (pageNumber: number) => void;
}

export const EmployerDocumentsSidePage = (props: EmployerDocumentsSidePageProps) => {
    const [selectedValue, setSelectedValue] = React.useState<EmployerNormalizedTag | undefined>();
    const [emptyContentTranslate, setEmptyContentTranslate] = React.useState(0);
    const track = useMetrics();
    const emptyContainerRef = React.useRef<HTMLDivElement>();

    React.useLayoutEffect(() => {
        if (emptyContainerRef.current) {
            const { height } = emptyContainerRef.current.getBoundingClientRect();
            setEmptyContentTranslate(Math.floor(height * 0.1) * -1);
        }
    }, [emptyContainerRef.current]);

    React.useLayoutEffect(() => {
        if (props.options?.warning != undefined) {
            track(EMPLOYER_DOCUMENTS_WARNING_SIZE, { value: props.options?.warning.length });
        }
    }, [props.options?.warning]);

    React.useEffect(() => {
        if (props.showSideBar) {
            track(EMPLOYER_DOCUMENTS_CALL_SOURCE, {
                value: props.options?.requestsTags?.flat().join(";"),
                title: props.options?.title,
            });
        }
    }, [props.showSideBar]);

    React.useEffect(() => {
        if (props.loading || props.documentTags.length === 0) {
            setSelectedValue(undefined);
        }
    }, [props.loading, props.documentTags]);

    const renderLoader = () => (
        // @ts-ignore
        <EmptyContainer ref={emptyContainerRef}>
            <EmptyContainerInner style={{ transform: `translateY(${emptyContentTranslate}px)` }}>
                <Spinner dimmed type={"big"} caption={"Загрузка документов"} />
            </EmptyContainerInner>
        </EmptyContainer>
    );

    const renderEmptyDocuments = () => (
        // @ts-ignore
        <EmptyContainer ref={emptyContainerRef}>
            <EmptyContainerInner style={{ transform: `translateY(${emptyContentTranslate}px)` }}>
                <IconContainer>
                    <img src={EmptyIcon} alt={"Документов ещё нет"} />
                </IconContainer>
                <EmptyText>Документов ещё нет</EmptyText>
            </EmptyContainerInner>
        </EmptyContainer>
    );

    const renderDocumentsTable = () => {
        track(EMPLOYER_DOCUMENTS_COUNT, { value: props.documentTags.length });
        return (
            <div>
                {props.options?.hint && <Hint>{props.options.hint}</Hint>}
                <EmployerDocumentsTable
                    normalizedDocuments={props.documentTags}
                    columns={props.options?.columns ?? []}
                    value={selectedValue}
                    activePage={props.activePage}
                    onSelect={setSelectedValue}
                    onPageChange={props.onPageChange}
                />
            </div>
        );
    };

    const renderContentWithFilter = () => {
        if (props.loading) {
            return renderLoader();
        }

        if (props.documentTags.length === 0) {
            return renderEmptyDocuments();
        }

        if (props.documentTags.length > 0) {
            return renderDocumentsTable();
        }

        return null;
    };

    const renderContent = () => {
        if (props.options?.filter) {
            return (
                <DocumentsContainer>
                    {props.options?.description && (
                        <DescriptionContainer>{props.options.description}</DescriptionContainer>
                    )}
                    <EmployerDocumentsFilter filterOptions={props.options.filter} />
                    {renderContentWithFilter()}
                </DocumentsContainer>
            );
        }

        if (props.loading) {
            return renderLoader();
        }

        if (props.documentTags.length === 0) {
            return renderEmptyDocuments();
        }

        if (props.documentTags.length > 0) {
            return (
                <DocumentsContainer>
                    {props.options?.description && (
                        <DescriptionContainer>{props.options.description}</DescriptionContainer>
                    )}
                    {renderDocumentsTable()}
                </DocumentsContainer>
            );
        }

        return null;
    };

    const renderSidePage = () => (
        <SidePage
            onClose={() => {
                track(EMPLOYER_DOCUMENTS_CLOSE);
                props.onClose();
            }}
            onOpened={() => {
                setSelectedValue(undefined);
            }}
            ignoreBackgroundClick={true}
            blockBackground={true}
            width={600}>
            <SidePage.Header>{props.options?.title ?? "Документы"}</SidePage.Header>
            <SidePage.Body>
                <SidePage.Container style={{ height: "100%", paddingRight: "24px" }}>
                    {!props.loading && props.options?.warning && (
                        <Warning>
                            <ScrollContainer maxHeight={175}>
                                <Markdown components={{ ul: ({ children }) => <Ul>{children}</Ul> }}>
                                    {props.options.warning}
                                </Markdown>
                            </ScrollContainer>
                        </Warning>
                    )}
                    {renderContent()}
                </SidePage.Container>
            </SidePage.Body>
            <SidePage.Footer panel>
                <Gapped gap={8}>
                    <Button
                        use="primary"
                        size="medium"
                        style={{ width: "160px" }}
                        disabled={selectedValue == undefined}
                        onClick={() => {
                            track(EMPLOYER_DOCUMENT_IMPORT, { value: selectedValue?.sourceTag });
                            props.onSubmit(selectedValue as EmployerNormalizedTag);
                        }}>
                        Добавить
                    </Button>
                    <Button
                        use="default"
                        size="medium"
                        onClick={() => {
                            track(EMPLOYER_DOCUMENTS_CANCEL);
                            setSelectedValue(undefined);
                            props.onClose();
                        }}>
                        Отменить
                    </Button>
                </Gapped>
            </SidePage.Footer>
        </SidePage>
    );

    return props.showSideBar ? renderSidePage() : null;
};
